/**
 * Auth Client — Frontend interface for auth operations via the backend API.
 * Replaces direct db.auth calls.
 */

import { apiFetch, setTokens, clearTokens, getAccessToken } from "./api-client";

export interface AuthUser {
  id: string;
  email: string;
  orgId: string | null;
  role: string | null;
  fullName: string | null;
}

interface LoginResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

interface SignupResponse {
  message: string;
}

interface MeResponse {
  id: string;
  email: string;
  orgId: string | null;
  orgName: string | null;
  role: string | null;
}

export const authClient = {
  async login(email: string, password: string): Promise<{ user: AuthUser; error: string | null }> {
    try {
      const data = await apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      setTokens(data.accessToken, data.refreshToken);
      return { user: data.user, error: null };
    } catch (err: any) {
      return { user: null as any, error: err.message || "Erro ao fazer login" };
    }
  },

  async signup(
    email: string,
    password: string,
    metadata?: { fullName?: string; companyName?: string; phone?: string }
  ): Promise<{ error: string | null }> {
    try {
      await apiFetch<SignupResponse>("/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          fullName: metadata?.fullName,
          companyName: metadata?.companyName,
          phone: metadata?.phone,
        }),
      });

      return { error: null };
    } catch (err: any) {
      return { error: err.message || "Erro ao criar conta" };
    }
  },

  async confirmEmail(email: string, code: string): Promise<{ error: string | null }> {
    try {
      await apiFetch("/auth/confirm", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      });
      return { error: null };
    } catch (err: any) {
      return { error: err.message || "Código inválido" };
    }
  },

  async resendCode(email: string): Promise<{ error: string | null }> {
    try {
      await apiFetch("/auth/resend-code", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      return { error: null };
    } catch (err: any) {
      return { error: err.message || "Erro ao reenviar código" };
    }
  },

  async forgotPassword(email: string): Promise<{ error: string | null }> {
    try {
      await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      return { error: null };
    } catch (err: any) {
      return { error: err.message || "Erro ao enviar email" };
    }
  },

  async resetPassword(email: string, code: string, newPassword: string): Promise<{ error: string | null }> {
    try {
      await apiFetch("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ email, code, newPassword }),
      });
      return { error: null };
    } catch (err: any) {
      return { error: err.message || "Erro ao redefinir senha" };
    }
  },

  async acceptInvite(
    token: string,
    fullName: string,
    password: string
  ): Promise<{ user: AuthUser; error: string | null }> {
    try {
      const data = await apiFetch<LoginResponse>("/auth/accept-invite", {
        method: "POST",
        body: JSON.stringify({ token, fullName, password }),
      });

      setTokens(data.accessToken, data.refreshToken);
      return { user: data.user, error: null };
    } catch (err: any) {
      return { user: null as any, error: err.message || "Erro ao aceitar convite" };
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ error: string | null }> {
    try {
      await apiFetch("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      return { error: null };
    } catch (err: any) {
      return { error: err.message || "Erro ao alterar senha" };
    }
  },

  async getMe(): Promise<AuthUser | null> {
    const token = getAccessToken();
    if (!token) return null;

    try {
      const data = await apiFetch<MeResponse>("/auth/me");
      return {
        id: data.id,
        email: data.email,
        orgId: data.orgId,
        role: data.role,
        fullName: null,
      };
    } catch {
      return null;
    }
  },

  async signOut(): Promise<void> {
    try {
      await apiFetch("/auth/signout", { method: "POST" });
    } catch {
      // Ignore — clean up local state anyway
    }
    clearTokens();
  },

  isAuthenticated(): boolean {
    return !!getAccessToken();
  },
};
